import api from './client';

// Enums
export enum ExpenseCategory {
  FEED = 'FEED',
  MEDICINE = 'MEDICINE', 
  EQUIPMENT = 'EQUIPMENT',
  UTILITIES = 'UTILITIES',
  RENT = 'RENT',
  SALARY = 'SALARY',
  MAINTENANCE = 'MAINTENANCE',
  TRANSPORTATION = 'TRANSPORTATION',
  MARKETING = 'MARKETING',
  OTHER = 'OTHER'
}

export enum ExpenseStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED'
}

export enum PaymentMethod {
  CASH = 'CASH',
  CREDIT_CARD = 'CREDIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CHECK = 'CHECK',
  MOBILE_PAYMENT = 'MOBILE_PAYMENT',
  OTHER = 'OTHER',
}

// Interfaces
export interface Expense {
  id: string;
  transactionDate: string;
  category: ExpenseCategory;
  description: string;
  amount: number;
  status: ExpenseStatus;
  paymentMethod: PaymentMethod;
  receiptNumber?: string;
  vendorId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseDto {
  transactionDate: string;
  category: ExpenseCategory;
  description: string;
  amount: number;
  status: ExpenseStatus;
  paymentMethod: PaymentMethod;
  receiptNumber?: string;
  vendorId?: string;
  notes?: string;
}

export interface UpdateExpenseDto {
  transactionDate?: string;
  category?: ExpenseCategory;
  description?: string;
  amount?: number;
  status?: ExpenseStatus;
  paymentMethod?: PaymentMethod;
  receiptNumber?: string;
  vendorId?: string;
  notes?: string;
}

export interface GetExpensesDto {
  category?: ExpenseCategory;
  status?: ExpenseStatus;
  paymentMethod?: PaymentMethod;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  vendorId?: string;
  description?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
}

export interface ExpensesAnalytics {
  totalExpensesAmount: number;
  totalExpensesCount: number;
  expensesAmountByCategory: Record<string, number>;
  expensesCountByCategory: Record<string, number>;
  expensesAmountByStatus: Record<string, number>;
  expensesCountByStatus: Record<string, number>;
}

export interface ExpensesByStatusAnalytics {
  totalExpenses: number;
  expensesByStatus: Record<string, number>;
  expensesCount: number;
}

// API functions
export const expensesApi = {
  // Get all expenses
  async getExpenses(filters: GetExpensesDto = {}): Promise<Expense[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = `accounting/expenses${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<Expense[]>(url);
    return response.data;
  },

  // Get expense by ID
  async getExpense(id: string): Promise<Expense> {
    const response = await api.get<Expense>(`accounting/expenses/${id}`);
    return response.data;
  },

  // Create expense
  async createExpense(data: CreateExpenseDto): Promise<Expense> {
    const response = await api.post<Expense>('accounting/expenses', data);
    return response.data;
  },

  // Update expense
  async updateExpense(id: string, data: UpdateExpenseDto): Promise<Expense> {
    const response = await api.put<Expense>(`accounting/expenses/${id}`, data);
    return response.data;
  },

  // Delete expense
  async deleteExpense(id: string): Promise<void> {
    await api.delete(`accounting/expenses/${id}`);
  },

  // Get expenses analytics
  async getExpensesAnalytics(): Promise<ExpensesAnalytics> {
    const response = await api.get<ExpensesAnalytics>('accounting/expenses/analytics');
    return response.data;
  },

  // Get expenses by status
  async getExpensesByStatus(): Promise<ExpensesByStatusAnalytics> {
    const response = await api.get<ExpensesByStatusAnalytics>('accounting/expenses/status');
    return response.data;
  },

  // Get total expenses
  async getTotalExpenses(): Promise<{ total: number }> {
    const response = await api.get<{ total: number }>('accounting/expenses/total');
    return response.data;
  }
};

// Helper functions
export const formatCurrency = (amount: number, currency = 'ZAR'): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

export const getExpenseStatusColor = (status: ExpenseStatus): string => {
  switch (status) {
    case ExpenseStatus.PENDING:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case ExpenseStatus.PAID:
      return 'bg-green-100 text-green-800 border-green-200';
    case ExpenseStatus.CANCELLED:
      return 'bg-red-100 text-red-800 border-red-200';
    case ExpenseStatus.REFUNDED:
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const getExpenseCategoryDisplay = (category: ExpenseCategory): string => {
  switch (category) {
    case ExpenseCategory.FEED:
      return 'Feed';
    case ExpenseCategory.MEDICINE:
      return 'Medicine';
    case ExpenseCategory.EQUIPMENT:
      return 'Equipment';
    case ExpenseCategory.UTILITIES:
      return 'Utilities';
    case ExpenseCategory.RENT:
      return 'Rent';
    case ExpenseCategory.SALARY:
      return 'Salary';
    case ExpenseCategory.MAINTENANCE:
      return 'Maintenance';
    case ExpenseCategory.TRANSPORTATION:
      return 'Transportation';
    case ExpenseCategory.MARKETING:
      return 'Marketing';
    case ExpenseCategory.OTHER:
      return 'Other';
    default:
      return category;
  }
};

export const getPaymentMethodDisplay = (method: PaymentMethod): string => {
  switch (method) {
    case PaymentMethod.CASH:
      return 'Cash';
    case PaymentMethod.CREDIT_CARD:
      return 'Credit Card';
    case PaymentMethod.BANK_TRANSFER:
      return 'Bank Transfer';
    case PaymentMethod.CHECK:
      return 'Check';
    case PaymentMethod.MOBILE_PAYMENT:
      return 'Mobile Payment';
    case PaymentMethod.OTHER:
      return 'Other';
    default:
      return method;
  }
};

export const calculateExpenseTotal = (expenses: Expense[]): number => {
  return expenses.reduce((total, expense) => total + expense.amount, 0);
};

export const generateExpenseNumber = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `EXP-${year}${month}${day}-${random}`;
}; 